import { showProgressDialog } from '@juvare/common-web-ui/DialogHelper';
import { globalHeaderHeightNormal } from '@juvare/common-web-ui/GlobalHeader/constants';
import { PageHeaderWithImage } from '@juvare/common-web-ui/PageHeader';
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { PowerBiEmbedToken, PowerBiReport } from '@juvare/platform-app/Integration';
import { usePowerBiReportClient } from '@juvare/platform-sdk-react/ui/hooks';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { Embed } from 'embed';
import { PowerBIEmbed } from 'powerbi-client-react';
import { Permissions, TokenType, ViewMode } from 'powerbi-models';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../components/Context';

export const ReportsPanel: React.FC = () => {
    const { userState } = React.useContext(AppContext);
    const { t, i18n } = useTranslation();
    const reportClient = usePowerBiReportClient();
    const [selectedReportId, setSelectedReportId] = React.useState('');
    const [availableReports, setAvailableReports] = React.useState<PowerBiReport[]>();
    const [reportToken, setReportToken] = React.useState<PowerBiEmbedToken>();
    const embeddedComponent = React.useRef<Embed>(null);
    const [embeddedComponentKey, setEmbeddedComponentKey] = React.useState(Date.now());
    const [refresh, setRefresh] = React.useState<unknown>();
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        reportClient
            .listReports()
            .then((response) => {
                setAvailableReports(response);
            })
            .catch(handleErrorSnackbar)
            .finally(() => setLoading(false));
    }, [reportClient, t]);
    React.useEffect(() => {
        if (selectedReportId && userState) {
            setReportToken(undefined);
            showProgressDialog({
                title: t('common.loading'),
                callback: (done) => {
                    reportClient
                        .getEmbedToken(selectedReportId)
                        .then((response) => {
                            setReportToken(response);
                            setEmbeddedComponentKey(Date.now());
                        })
                        .catch(handleErrorSnackbar)
                        .finally(() => done());
                },
            });
        }
    }, [reportClient, selectedReportId, t, refresh, userState]);
    React.useEffect(() => {
        let timeOutHandle: number;
        if (reportToken) {
            timeOutHandle = window.setTimeout(
                () => setRefresh({}),
                new Date(reportToken.expiration).getTime() - Date.now() - 10 * 60 * 1000,
            );
        }
        return () => {
            if (timeOutHandle != null) {
                window.clearTimeout(timeOutHandle);
            }
        };
    }, [reportToken]);
    React.useEffect(() => {
        if (embeddedComponent?.current?.config.settings) {
            embeddedComponent.current.config.settings.localeSettings = {
                language: i18n.language,
                formatLocale: i18n.language,
            };
            setEmbeddedComponentKey(Date.now());
        }
    }, [i18n.language]);
    return (
        <Box
            sx={{
                height: `calc(100vh - ${globalHeaderHeightNormal + 52}px)`,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <PageHeaderWithImage gradient='blue-dark-angle' title={t('panels.reports.title')} />
            {loading && (
                <Typography p={1} variant='body1'>
                    {t('common.loading')}
                </Typography>
            )}
            {availableReports && (
                <FormControl
                    sx={{
                        width: 304,
                        m: 2,
                    }}
                    variant='outlined'
                    size='small'
                >
                    <InputLabel>{t('panels.reports.availableReports')}</InputLabel>
                    <Select
                        label={t('panels.reports.availableReports')}
                        value={selectedReportId}
                        onChange={(event) => setSelectedReportId(event.target.value as string)}
                        inputProps={{ 'aria-label': t('panels.reports.availableReports') }}
                    >
                        {availableReports.map((report) => {
                            return (
                                <MenuItem key={report.id} value={report.id}>
                                    {report.name}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            )}
            {selectedReportId && (
                <PowerBIEmbed
                    key={embeddedComponentKey}
                    embedConfig={{
                        type: 'report',
                        id: selectedReportId,
                        embedUrl: 'https://app.powerbi.com/reportEmbed',
                        accessToken: reportToken?.token,
                        tokenType: TokenType.Embed,
                        permissions: Permissions.Read,
                        viewMode: ViewMode.View,
                        settings: {
                            localeSettings: {
                                language: i18n.language,
                                formatLocale: i18n.language,
                            },
                            panes: {
                                pageNavigation: {
                                    visible: false,
                                },
                            },
                        },
                    }}
                    getEmbeddedComponent={(embeddedCmp) => (embeddedComponent.current = embeddedCmp)}
                    cssClassName='PowerBIEmbed'
                    eventHandlers={
                        new Map([
                            [
                                'error',
                                function (event) {
                                    console.error(event);
                                },
                            ],
                        ])
                    }
                />
            )}
        </Box>
    );
};
